import { findMathContentRanges } from '@/shared/features/rich-text/utils/parseMathContent';
import { buildSourceBackedHitTermSegments } from '@/shared/quizList/utils/buildSourceBackedHighlightSegments';

const TAG = /(<[^>]*>)/;
const ENTITY = /(&(?:#\d+|#x[0-9a-f]+|[a-z]+);)/i;

/** `[평문, 불투명, 평문, …]` 의 짝수 자리만 변환해 다시 잇는다. */
const mapPlain = (parts: string[], transform: (plain: string) => string) =>
  parts
    .map((part, index) => (index % 2 !== 0 ? part : transform(part)))
    .join('');

/** `$…$`·ZWSP 수식 구간을 캡처 그룹 `split` 과 같은 번갈이 배열로 낸다. */
const splitByMath = (text: string): string[] => {
  const parts: string[] = [];
  let cursor = 0;
  for (const { start, end } of findMathContentRanges(text)) {
    parts.push(text.slice(cursor, start), text.slice(start, end));
    cursor = end;
  }
  return [...parts, text.slice(cursor)];
};

const markPlain = (plain: string, hitTerms: readonly string[]) =>
  buildSourceBackedHitTermSegments(plain, hitTerms)
    ?.map(segment =>
      segment.type === 'hit'
        ? `<mark data-search-hit>${segment.value}</mark>`
        : segment.value,
    )
    .join('') ?? plain;

/**
 * 서버가 준 hit 문자열(정확 일치)을 Tiptap HTML 의 텍스트 노드에만 `<mark data-search-hit>` 으로
 * 감싼다. 태그·`$…$` 수식·엔티티 안에는 넣지 않는다.
 */
export const injectHitMarksIntoHtml = (
  html: string,
  hitTerms: readonly string[],
): string => {
  const terms = hitTerms.filter(Boolean);
  if (terms.length === 0) return html;

  // 수식 범위를 먼저 보호한다 — 렌더러(StaticRichTextRenderer)도 태그를 포함한 HTML 전체에서
  // `$…$` 를 찾으므로, 태그를 가로지르는 수식(`$<strong>x</strong>$`)을 같은 범위로 봐야 한다.
  return mapPlain(splitByMath(html), nonMath =>
    mapPlain(nonMath.split(TAG), text =>
      mapPlain(text.split(ENTITY), run => markPlain(run, terms)),
    ),
  );
};
